import { readdir } from "node:fs/promises";
import { basename, extname, join } from "node:path";
import type { LintFinding, LintRule } from "../types.js";

const WIKI_LINK = /\[\[([^\]|#]+)(?:[|#][^\]]*)?\]\]/g;

export interface WikiLinkResolvesOptions {
  /** Directory under which valid link targets live. Searched recursively. */
  readonly searchRoot: string;
  /**
   * Target file extensions. Defaults to `[".md"]`.
   *
   * `.md` targets are keyed by basename (extension stripped) — `[[Foo]]`
   * resolves `Foo.md`. Any non-`.md` extension listed here keeps its
   * extension in the index, so `[[Foo.pdf]]` resolves `Foo.pdf` while a bare
   * `[[Foo]]` never silently matches an attachment. Mirrors the resolution
   * contract in `@vortex-os/link-rewriter` (`resolve.ts`). Pass attachment
   * extensions here (e.g. `[".md", ".pdf", ".png"]`) so imported binaries are
   * treated as valid link targets instead of false "unresolved" findings.
   */
  readonly extensions?: readonly string[];
}

/**
 * Rule that checks wiki-style links `[[target]]` resolve to an existing
 * file under `searchRoot` (by basename match, recursive). Aliases
 * (`[[target|alias]]`) and anchors (`[[target#section]]`) are stripped
 * before lookup.
 *
 * The target index is built lazily on the first invocation and cached for
 * the lifetime of the rule instance.
 */
export function wikiLinkResolves(options: WikiLinkResolvesOptions): LintRule {
  let cache: Set<string> | undefined;
  const extensions = options.extensions ?? [".md"];

  async function buildIndex(): Promise<Set<string>> {
    if (cache) return cache;
    const out = new Set<string>();
    const stack: string[] = [options.searchRoot];
    while (stack.length > 0) {
      const current = stack.pop();
      if (current === undefined) break;
      let entries;
      try {
        entries = await readdir(current, { withFileTypes: true });
      } catch {
        continue;
      }
      for (const entry of entries) {
        const full = join(current, entry.name);
        if (entry.isDirectory()) {
          stack.push(full);
        } else if (entry.isFile()) {
          const ext = extname(entry.name);
          if (!extensions.includes(ext)) continue;
          // `.md` is keyed by basename so `[[Foo]]` resolves `Foo.md`. Any
          // other listed extension keeps its full name so `[[Foo.pdf]]`
          // resolves `Foo.pdf` while a bare `[[Foo]]` never silently matches
          // an attachment. Mirrors link-rewriter resolve.ts. (The previous
          // code stripped EVERY extension, so listing a non-md extension was
          // a no-op — the key `Foo` never matched the link target `Foo.pdf`.)
          out.add(ext === ".md" ? basename(entry.name, ".md") : entry.name);
        }
      }
    }
    cache = out;
    return out;
  }

  return {
    id: "wiki-link-resolves",
    description: "Check [[wiki links]] resolve to an existing file by basename",
    async check({ file, content }) {
      const findings: LintFinding[] = [];
      const index = await buildIndex();
      const lines = content.split("\n");
      lines.forEach((line, idx) => {
        for (const match of line.matchAll(WIKI_LINK)) {
          const target = match[1]?.trim();
          if (target && !index.has(target)) {
            findings.push({
              rule: "wiki-link-resolves",
              severity: "warning",
              file,
              line: idx + 1,
              message: `Unresolved wiki link: [[${target}]]`,
            });
          }
        }
      });
      return findings;
    },
  };
}
